import {
  Badge,
  Box,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import { INotifications } from 'modules/Layout';
import moment from 'moment';
import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NotificationService } from 'services/NotificationService';
import {
  AppDangerButton,
  AppPrimaryButtonOutlined,
} from 'shared/components/AppComponents';
import { Toast, Utils } from 'shared/helpers';

import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import InventoryIcon from '@mui/icons-material/Inventory';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import LoopIcon from '@mui/icons-material/Loop';
import { actionOnNavigate } from 'shared/helpers/Notification';
import EqualizerIcon from '@mui/icons-material/Equalizer';
import IosShareIcon from '@mui/icons-material/IosShare';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';

const TYPE_FILTERS: any = [
  { key: 'alerts', label: 'Alerts' },
  { key: 'info', label: 'Info' },
];

const STATUS_FILTERS: any = [
  { key: 'unRead', label: 'Unread only' },
  { key: 'actionRequired', label: 'Action Required' },
  { key: 'resolved', label: 'Resolved' },
];

const NotificationList = (props: any): ReactElement => {
  const { setType, selectedNote, setSelectedNote } = props;
  const [notification, setNotification] = useState<INotifications[]>([]);
  const [isArchieved, setIsArchieved] = useState(false);
  const [isLoading, setIsloading] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [anchorElFilter, setAnchorElFilter] = useState<any>(null);
  const [openNotifDetails, setOpenNotifDetails] = useState<any>([]);
  const open = Boolean(anchorEl);
  const openFilter = Boolean(anchorElFilter);
  const navigate = useNavigate();

  const [filters, setFilters] = useState<any>({
    alerts: false,
    info: false,
    unRead: false,
    actionRequired: false,
    resolved: false,
  });

  const selectedFilters = useMemo(
    () => Object.keys(filters).filter((key) => filters[key]),
    [filters]
  );

  const handleOpenFilter = (event: any): void => {
    setAnchorElFilter(event.currentTarget);
  };

  const handleCloseFilter = (): void => {
    setAnchorElFilter(null);
  };

  const toggleFilter = (key: any): void => {
    setFilters((prev: any) => {
      const next = { ...prev, [key]: !prev[key] };
      return next;
    });
  };

  const renderFilterMenu = (key: any, label: string): any => (
    <MenuItem key={key} onClick={() => toggleFilter(key)}>
      <Checkbox
        checked={filters[key]}
        edge="start"
        size="small"
        tabIndex={-1}
        disableRipple
      />
      <ListItemText primary={label} />
    </MenuItem>
  );

  const getNotificationList = (read: string, archive: string): void => {
    setIsloading(true);
    const selectedFilter = selectedFilters
      .filter((key) => TYPE_FILTERS.some((item: any) => item.key === key))
      .join(',');

    const actionFilter = selectedFilters
      .filter((key) => STATUS_FILTERS.some((item: any) => item.key === key))
      .join(',');
    NotificationService.getNotificationList(
      read,
      archive,
      selectedFilter,
      actionFilter
    )
      .then((res) => {
        const { notifications } = res.data.data;
        const updatedNotification = notifications.map((d: INotifications) => ({
          ...d,
          isExpanded: false,
        }));
        setNotification(updatedNotification);
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
      })
      .finally(() => {
        setIsloading(false);
      });
  };

  const actionOnUpdateNotification = (payload: any, id: string): void => {
    const requestObj = {
      event: payload,
    };
    const actionMap = {
      read: { readAt: new Date().toISOString() },
      unread: { readAt: null },
    };

    let updatedNotification = [...notification];

    if (payload === 'archive' || payload === 'restore') {
      updatedNotification = updatedNotification.filter(
        (item) => item._id !== id
      );
    } else {
      updatedNotification = updatedNotification.map((item) =>
        item._id === id
          ? {
              ...item,
              ...(actionMap[payload as keyof typeof actionMap] || {}),
            }
          : item
      );
    }

    setNotification(updatedNotification);
    NotificationService.updateNotification(requestObj, id)
      .then(() => {
        if (payload === 'archive') {
          Toast.showSuccessMessage('Notification archived');
        }
        if (payload === 'restore') {
          Toast.showSuccessMessage('Notification restored');
        }
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
      });
  };
  const actionOnDelete = (id: string): void => {
    NotificationService.deleteNotification(id)
      .then((res) => {
        getNotificationList('', '');
        Utils.handleSuccessResponse(res);
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
      });
  };

  const actionOnOpenMenu = (event: any, Note: INotifications): void => {
    setAnchorEl(event.currentTarget);
    setSelectedNote(Note);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  const actionOnOpenArchived = (): void => {
    getNotificationList('', 'true');
    setIsArchieved(true);
  };

  const actionOnBack = (): void => {
    getNotificationList('', '');
    setIsArchieved(false);
  };

  const actionOnSetting = (): void => {
    setType('settings');
  };

  const getTimeAgo = (date: string): any => {
    const hoursDiff = moment().diff(moment(date), 'hours');

    if (hoursDiff >= 24) {
      return moment(date).fromNow();
    }

    return moment(date).startOf('hour').fromNow();
  };

  const getNoteIcon = (event: string): any => {
    if (event === 'policy_approvals') {
      return <WarningAmberOutlinedIcon fontSize="small" color="error" />;
    }
    return <InfoOutlinedIcon fontSize="small" />;
  };

  const getNoteValue = (event: string): string => {
    if (event === 'external_risk_assessment') {
      return 'External Risk Assessment';
    }
    if (event === 'training_assignment_completed') {
      return 'Training Assessment';
    }
    if (event === 'team_member_invite') {
      return 'Team Member';
    }
    return 'Policy';
  };

  const getActionIcon = (note: INotifications): ReactElement => {
    if (note.action === 'required') {
      return (
        <AppDangerButton
          className="btn-extra-small mt-1"
          title="Action Required"
        />
      );
    }

    if (note.event === 'report') {
      return (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            setOpenNotifDetails((prev: string[]) =>
              prev.includes(note._id)
                ? prev.filter((id) => id !== note._id)
                : [...prev, note._id]
            );
          }}
        >
          <KeyboardArrowDownIcon
            fontSize="small"
            sx={{
              transform: openNotifDetails.includes(note._id)
                ? 'rotate(180deg)'
                : 'rotate(0deg)',
              transition: 'transform 0.2s',
            }}
          />
        </IconButton>
      );
    }

    return (
      <IconButton
        sx={{
          '&:hover': {
            backgroundColor: 'transparent',
          },
        }}
      >
        <TaskAltOutlinedIcon fontSize="small" />
      </IconButton>
    );
  };

  // Hooks
  useEffect(() => {
    getNotificationList('', '');
  }, [filters]);

  // UI Elements
  return (
    <>
      <Box className="justify-align-space mb-5">
        {isArchieved && (
          <div>
            <AppPrimaryButtonOutlined
              startIcon={<KeyboardBackspaceIcon />}
              title="back"
              className="btn-extra-small"
              onClick={() => actionOnBack()}
            />
          </div>
        )}
        {!isArchieved && (
          <Badge badgeContent={selectedFilters.length} color="secondary">
            <AppPrimaryButtonOutlined
              startIcon={<TuneIcon />}
              title="Filter"
              className="btn-extra-small"
              onClick={handleOpenFilter}
            />
          </Badge>
        )}
        <Box />
        <div className="archive-actions">
          {!isArchieved && (
            <AppPrimaryButtonOutlined
              startIcon={<InventoryIcon />}
              title="Archive"
              className="btn-extra-small"
              onClick={actionOnOpenArchived}
            />
          )}
          <IconButton
            onClick={() => {
              getNotificationList('', isArchieved ? 'true' : '');
            }}
          >
            <LoopIcon fontSize="small" />
          </IconButton>
        </div>
      </Box>
      {isLoading && (
        <Box className="empty-content">
          <CircularProgress />
        </Box>
      )}
      {!isLoading &&
        notification.map((note, index) => (
          <Stack
            className="card-container"
            sx={{
              borderLeft:
                !note.readAt && !isArchieved
                  ? '3px solid #06362b'
                  : '1px solid gray',
              borderRight: '1px solid gray',
              borderTop: '1px solid gray',
              borderBottom: '1px solid gray',
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (note.event === 'report' && !isArchieved) {
                if (!note.readAt) {
                  actionOnUpdateNotification('read', note._id);
                }
                setNotification((prev) =>
                  prev.map((item, i) => ({
                    ...item,
                    isExpanded:
                      i === index ? !item.isExpanded : item.isExpanded,
                  }))
                );
              } else {
                if (!note.readAt) {
                  actionOnUpdateNotification('read', note._id);
                }
                actionOnNavigate(note, navigate);
              }
            }}
          >
            <Stack direction="row" spacing={2} alignItems="flex-start">
              {getNoteIcon(note.event)}

              <Stack flex={1} spacing={1}>
                <Typography
                  variant="body1"
                  className={!note.readAt && !isArchieved ? 'fw-8' : 'fw-5'}
                >
                  {note.title}
                </Typography>

                {!note.isExpanded && (
                  <Typography variant="body2">{note.descriptions}</Typography>
                )}

                <Stack direction="row" spacing={2} alignItems="center">
                  <Chip
                    label={note.tag ? note.tag : getNoteValue(note.event)}
                    size="small"
                  />
                  <Typography variant="body2">
                    {getTimeAgo(note.createdAt)}
                  </Typography>
                </Stack>
              </Stack>

              {isArchieved && (
                <AppPrimaryButtonOutlined
                  startIcon={<KeyboardBackspaceIcon />}
                  title="Restore"
                  className="btn-extra-small"
                  onClick={(e: any) => {
                    e.stopPropagation();
                    actionOnUpdateNotification('restore', note._id);
                  }}
                />
              )}
              {!isArchieved && (
                <>
                  {getActionIcon(note)}
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      actionOnUpdateNotification('archive', note._id);
                    }}
                  >
                    <Inventory2OutlinedIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      actionOnOpenMenu(e, note);
                    }}
                  >
                    <MoreHorizIcon />
                  </IconButton>
                </>
              )}
            </Stack>

            {openNotifDetails?.includes(note._id) && (
              <Stack>
                <Divider className="mb-4 mt-4" />
                <Stack
                  className=" mb-2"
                  direction="row"
                  alignItems="center"
                  spacing={2}
                >
                  <EqualizerIcon fontSize="medium" />
                  <Typography variant="h3">{note.descriptions}</Typography>
                </Stack>

                <Card className="app-card ">
                  {Object.entries(note.notificationEvents || {}).map(
                    ([key, value]) => (
                      <Stack key={key}>
                        <Stack
                          direction="row"
                          spacing={3}
                          className="p-4"
                          justifyContent="space-between"
                        >
                          <Typography variant="body1">{key}</Typography>
                          <Typography variant="body1">
                            {String(value)}
                          </Typography>
                        </Stack>
                        <Divider />
                      </Stack>
                    )
                  )}
                </Card>
                <Stack width="fit-content">
                  <AppPrimaryButtonOutlined
                    // fullWidth
                    startIcon={<IosShareIcon />}
                    title={`View ${note.title}`}
                    className="btn-small mt-6 mb-2"
                    onClick={(e: any) => {
                      e.stopPropagation();
                      actionOnNavigate(note, navigate);
                    }}
                  />
                </Stack>
              </Stack>
            )}
          </Stack>
        ))}
      {!isLoading && notification.length === 0 && (
        <Card className="empty-content">
          {isArchieved && (
            <>
              <Inventory2OutlinedIcon fontSize="large" />
              <Typography variant="h5" className="fw-6">
                Archive is empty.
              </Typography>
            </>
          )}
          {!isArchieved && (
            <>
              <NotificationsNoneOutlinedIcon fontSize="large" />
              <Typography variant="h5" className="fw-6">
                No Notifications Yet
              </Typography>
            </>
          )}
        </Card>
      )}
      {selectedNote && (
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          PaperProps={{
            sx: {
              width: 200,
              overflow: 'hidden',
            },
          }}
        >
          <MenuItem
            onClick={() => {
              const value = !selectedNote.readAt ? 'read' : 'unread';
              setAnchorEl(null);
              actionOnUpdateNotification(value, selectedNote._id);
            }}
          >
            Mark as {!selectedNote.readAt ? 'Read' : 'Unread'}
          </MenuItem>
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              actionOnUpdateNotification('archive', selectedNote._id);
            }}
          >
            Archive
          </MenuItem>
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              actionOnDelete(selectedNote._id);
            }}
          >
            Dismiss
          </MenuItem>
          <MenuItem onClick={() => actionOnNavigate(selectedNote, navigate)}>
            <IosShareIcon fontSize="small" />{' '}
            <Typography className="mx-1">Open Source</Typography>
          </MenuItem>
          {selectedNote.event === 'report' && (
            <MenuItem onClick={actionOnSetting}>
              <SettingsOutlinedIcon fontSize="small" />{' '}
              <Typography className="mx-1">Go to Settings</Typography>
            </MenuItem>
          )}
        </Menu>
      )}
      <Menu
        anchorEl={anchorElFilter}
        open={openFilter}
        onClose={handleCloseFilter}
        PaperProps={{
          sx: {
            width: 200,
            overflow: 'hidden',
          },
        }}
      >
        {TYPE_FILTERS.map((item: any) =>
          renderFilterMenu(item.key, item.label)
        )}

        <>
          <Divider sx={{ my: 0.5 }} />
          {STATUS_FILTERS.map((item: any) =>
            renderFilterMenu(item.key, item.label)
          )}
        </>
      </Menu>
    </>
  );
};

export default NotificationList;
